import styled from '@emotion/styled';
import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { SupportListItem } from './SupportListItem';
import { device } from '../../../../../utils';

const DONATION_LIMIT = 50;
const AUTO_START_ITEM = 10;
const SCROLL_PAUSE = 900;
const SCROLL_DURATION = 1000;

const getDonationKey = (item, index = 0) => {
  if (!item) return `empty-${index}`;

  return String(
    item.id ||
      item.donation_id ||
      item.payment_id ||
      item.intent_id ||
      item.created_at ||
      item.time ||
      `${item.first_name || ''}-${item.last_name || ''}-${
        item.custom_name || ''
      }-${item.amount || ''}-${item.city || ''}-${index}`
  );
};

const RecentSupport = ({
  projectId,
  getProjectDonations,
  donations,
  project,
  loadingDonations,
}) => {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const timeoutRef = useRef(null);
  const setupRafRef = useRef(null);
  const animationRafRef = useRef(null);
  const userInteractedRef = useRef(false);

  const donationList = useMemo(() => donations?.donations || [], [donations]);
  const scrollItems = useMemo(
    // The API returns newest first. Keep the latest supporter at the top and
    // automatically scroll upward from the tenth item toward the newest one.
    () => donationList.slice(0, DONATION_LIMIT),
    [donationList]
  );

  useEffect(() => {
    getProjectDonations(projectId, DONATION_LIMIT);
  }, [getProjectDonations, projectId]);

  useEffect(() => {
    userInteractedRef.current = false;

    setupRafRef.current = requestAnimationFrame(() => {
      const viewport = viewportRef.current;
      const track = trackRef.current;

      if (viewport && track) {
        const startIndex = Math.min(AUTO_START_ITEM - 1, track.children.length - 1);
        const startRow = track.children[startIndex];

        if (startRow) {
          viewport.scrollTop =
            startRow.getBoundingClientRect().top -
            track.getBoundingClientRect().top;
        }
      }
    });

    return () => cancelAnimationFrame(setupRafRef.current);
  }, [scrollItems]);

  useEffect(() => {
    if (scrollItems.length < 2 || userInteractedRef.current) {
      return;
    }

    const scheduleNextScroll = () => {
      timeoutRef.current = setTimeout(runNextScroll, SCROLL_PAUSE);
    };

    const runNextScroll = () => {
      const viewport = viewportRef.current;
      const track = trackRef.current;

      if (!viewport || !track || userInteractedRef.current) {
        return;
      }

      if (viewport.scrollTop <= 1) {
        return;
      }

      const trackTop = track.getBoundingClientRect().top;
      const rowTops = Array.from(track.children).map(
        (row) => row.getBoundingClientRect().top - trackTop
      );
      const targetTop = [...rowTops]
        .reverse()
        .find((top) => top < viewport.scrollTop - 1);

      if (targetTop === undefined) {
        return;
      }

      const startTop = viewport.scrollTop;
      const distance = targetTop - startTop;
      let startTime;

      const animate = (timestamp) => {
        if (userInteractedRef.current) {
          return;
        }

        if (startTime === undefined) {
          startTime = timestamp;
        }

        const progress = Math.min((timestamp - startTime) / SCROLL_DURATION, 1);
        const easedProgress =
          progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        viewport.scrollTop = startTop + distance * easedProgress;

        if (progress < 1) {
          animationRafRef.current = requestAnimationFrame(animate);
        } else {
          scheduleNextScroll();
        }
      };

      animationRafRef.current = requestAnimationFrame(animate);
    };

    scheduleNextScroll();

    return () => {
      clearTimeout(timeoutRef.current);
      cancelAnimationFrame(animationRafRef.current);
    };
  }, [scrollItems.length]);

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
      cancelAnimationFrame(setupRafRef.current);
      cancelAnimationFrame(animationRafRef.current);
    };
  }, []);

  const stopAutoScroll = () => {
    userInteractedRef.current = true;
    clearTimeout(timeoutRef.current);
    cancelAnimationFrame(animationRafRef.current);
  };

  const viewportHeight = 288;

  const shouldShowMoreSupport =
    donationList.length === 1 || donationList.length === 2;

  if (loadingDonations && donationList.length < 1) {
    return (
      <Wrapper>
        <Header>
          {project.is_ticketable
            ? 'Die neuesten Buchungen'
            : 'Die neuesten Unterstützungen'}
        </Header>
        <Viewport $height={viewportHeight} />
      </Wrapper>
    );
  }

  if (donationList.length < 1) {
    return (
      <NoSupportContent>
        <NoSupportTitle>
          „…Und was ihr an Gutem spendet, soll euch erstattet werden…“ [2:272]
        </NoSupportTitle>

        <NoSupportSubtitle>
          Noch hat keiner das Projekt unterstützt.
        </NoSupportSubtitle>

        <NoSupportSubtitle>
          Sei der/die erste Unterstützer/in!
        </NoSupportSubtitle>

        <NoSupportIllustration
          src="/supportIllustration.png"
          alt="No support illustration"
        />
      </NoSupportContent>
    );
  }

  return (
    <Wrapper>
      <Header>{project.is_ticketable ? "Die neuesten Buchungen" :"Die neuesten Unterstützungen"}</Header>

      <Viewport
        ref={viewportRef}
        $height={!shouldShowMoreSupport ? viewportHeight : undefined}
        onWheel={stopAutoScroll}
        onTouchStart={stopAutoScroll}
        onPointerDown={stopAutoScroll}
        onKeyDown={stopAutoScroll}
        tabIndex={0}
      >
        <Track ref={trackRef}>
          {scrollItems.map((item, index) => (
            <RowItem key={getDonationKey(item, index)}>
              <SupportListItem
                {...item}
                project={project}
                city={item.city || ''}
                amount={item.amount || 0}
                custom_message={item.custom_message}
                message={item.message}
                custom_city={item.custom_city}
                selected_avatar={item.selected_avatar}
                custom_name={item.custom_name}
                ticketCount={item.ticketCount || item.ticket_count}
                name={
                  item.last_name || item.first_name
                    ? `${item.first_name || ''} ${item.last_name || ''}`.trim()
                    : false
                }
              />
            </RowItem>
          ))}

          {shouldShowMoreSupport && (
            <RowItem>
              <MoreSupportBox>
                <EmptySupportTitle>Hier geht noch mehr...</EmptySupportTitle>
                <EmptySupportSubTitle>
                  Lass Deine Sadaqah da und treibe das Projekt voran!
                </EmptySupportSubTitle>
              </MoreSupportBox>
            </RowItem>
          )}
        </Track>
      </Viewport>
    </Wrapper>
  );
};

RecentSupport.propTypes = {
  getProjectDonations: PropTypes.func.isRequired,
  donations: PropTypes.object.isRequired,
  projectId: PropTypes.number.isRequired,
  project: PropTypes.object,
  loadingDonations: PropTypes.bool.isRequired,
};

export default RecentSupport;

const Wrapper = styled.div`
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  width: 100%;
  max-width: 100%;
  height: 332px;

  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;

  @media ${device.allMobile} {
    width: 100%;
  }
`;

const Header = styled.div`
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  align-items: flex-start;

  width: 100%;
  height: 44px;

  padding: 12px 16px;
  gap: 4px;

  background: #f5fbee;
  border-bottom: 1px solid #e5e7eb;

  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;

  color: #3f621a;
  text-align: left;
`;

const Viewport = styled.div`
  box-sizing: border-box;

  width: 100%;
  height: 288px;
  max-height: 288px;

  padding: 12px 16px;

  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
  text-align: left;

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const Track = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  width: 100%;
  gap: 12px;
`;

const RowItem = styled.div`
  width: 100%;
  flex: 0 0 auto;
`;

const MoreSupportBox = styled.div`
  box-sizing: border-box;

  width: 100%;
  min-height: 80px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  padding: 16px;

  background: #ffffff;
  border: 1px solid #f3f4f6;
  border-radius: 10px;

  text-align: center;
`;

const EmptySupportTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #717680;
`;

const EmptySupportSubTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #717680;
`;

const NoSupportContent = styled.div`
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 20px 16px;
  gap: 4px;

  isolation: isolate;
  position: relative;

  width: 100%;
  max-width: 100%;
  min-height: 240px;

  margin-bottom: 105px;

  background: rgba(255, 255, 255, 0.02);
  border: 1px solid #f5f5f5;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.09);
  backdrop-filter: blur(2px);
  border-radius: 8px;
  overflow: hidden;

  @media ${device.allMobile} {
    margin-bottom: 0;
  }
`;

const NoSupportTitle = styled.p`
  width: 100%;
  max-width: 448px;
  margin: 0;

  font-family: 'Crimson Text', serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;

  text-align: center;
  color: #3f621a;

  position: relative;
  z-index: 2;
`;

const NoSupportSubtitle = styled.p`
  width: 100%;
  max-width: 448px;
  margin: 0;

  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;

  text-align: center;
  color: #535862;

  position: relative;
  z-index: 2;
`;

const NoSupportIllustration = styled.img`
  position: absolute;

  width: 286px;
  height: 177px;

  left: -64px;
  top: 87px;

  object-fit: contain;
  mix-blend-mode: darken;

  z-index: 1;
  pointer-events: none;
`;
